import type { MerchantChatMessage } from '../model/schema';

/**
 * Date separators for the thread, ported from the old window's
 * `groupMessagesByDate` + `formatDate`.
 *
 * Two deliberate divergences: the group key is built with `Intl` instead of
 * `date-fns` (one less dependency for a numeric stamp), and the label renders
 * in the UI locale instead of the old window's hardcoded Russian (it called
 * `dayjs.locale(ru)` as a module side effect).
 */

/** Numeric `DD.MM.YYYY` group key — stable across locales. */
export function merchantMessageGroupKey(createdAt: string): string {
  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) return 'unknown';
  const p = (n: number) => n.toString().padStart(2, '0');
  return `${p(date.getDate())}.${p(date.getMonth() + 1)}.${date.getFullYear()}`;
}

/**
 * Uzbek month names. Browsers' ICU data for `uz` is incomplete — `Intl` renders
 * October as the placeholder `M10` — so the label is built by hand, in the same
 * `14-oktabr` shape the rest of the app's uz dates use.
 */
const UZ_MONTHS = [
  'yanvar',
  'fevral',
  'mart',
  'aprel',
  'may',
  'iyun',
  'iyul',
  'avgust',
  'sentabr',
  'oktabr',
  'noyabr',
  'dekabr',
];

/**
 * `5 October`, with the year when it isn't this one. `locale` is the
 * `i18n.language` tag (`ru`/`uz`/`en`).
 */
export function formatMerchantGroupLabel(groupKey: string, locale: string): string {
  const [day, month, year] = groupKey.split('.').map(Number);
  if (!day || !month || !year) return '…';
  const date = new Date(year, month - 1, day);
  const sameYear = year === new Date().getFullYear();
  if (locale.startsWith('uz')) {
    const label = `${day}-${UZ_MONTHS[month - 1]}`;
    return sameYear ? label : `${label}, ${year}`;
  }
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'long',
    ...(sameYear ? {} : { year: 'numeric' }),
  }).format(date);
}

/** Group newest-first messages under their date separators, order kept. */
export function groupMerchantMessagesByDate(
  messages: MerchantChatMessage[],
): [groupKey: string, messages: MerchantChatMessage[]][] {
  const groups = new Map<string, MerchantChatMessage[]>();
  for (const message of messages) {
    const key = merchantMessageGroupKey(message.created_at);
    const group = groups.get(key);
    if (group) group.push(message);
    else groups.set(key, [message]);
  }
  return [...groups.entries()];
}
